// Types of the student administrative-document requests (attestations) —
// student side and admin side. Mirrors backend/src/modules/student-requests.
export enum AttestationType {
  PRE_INSCRIPTION = 'PRE_INSCRIPTION',
  ATTESTATION_SCOLARITE = 'ATTESTATION_SCOLARITE',
  RELEVE_NOTES_ANNUEL = 'RELEVE_NOTES_ANNUEL',
  ATTESTATION_REUSSITE = 'ATTESTATION_REUSSITE',
  ATTESTATION_INSCRIPTION = 'ATTESTATION_INSCRIPTION',
  CERTIFICAT_ASSIDUITE = 'CERTIFICAT_ASSIDUITE',
  RELEVE_NOTES_SEMESTRIEL = 'RELEVE_NOTES_SEMESTRIEL',
  ATTESTATION_STAGE = 'ATTESTATION_STAGE',
  CONVENTION_STAGE = 'CONVENTION_STAGE', // Downloads template from institutional documents (not generated)
}

export enum RequestStatus {
  DRAFT = 'DRAFT',
  PENDING_FINANCE = 'PENDING_FINANCE',
  PENDING_ACADEMIC = 'PENDING_ACADEMIC',
  FINANCE_APPROVED = 'FINANCE_APPROVED',
  ACADEMIC_APPROVED = 'ACADEMIC_APPROVED',
  APPROVED = 'APPROVED',
  GENERATED = 'GENERATED',
  DOWNLOADED_FOR_PRINT = 'DOWNLOADED_FOR_PRINT',
  PRINTED = 'PRINTED',
  DELIVERED = 'DELIVERED',
  REJECTED_FINANCE = 'REJECTED_FINANCE',
  REJECTED_ACADEMIC = 'REJECTED_ACADEMIC',
  CANCELLED = 'CANCELLED',
}

/** "Look twice" flag on a request: jury decision of a réussite + academic reasons. */
export interface AcademicAttention {
  juryDecision: string | null;
  reasons: string[];
}

export interface StudentRequest {
  id: string;
  tenantId: string;
  studentId: string;
  academicYearId: string;
  requestType: AttestationType;
  referenceNumber: string;
  status: RequestStatus;
  purpose?: string;

  // Finance validation
  financeValidationRequired: boolean;
  financeAutoApproved: boolean;
  financeValidatedBy?: string;
  financeValidatedAt?: string;
  financeRejectionReason?: string;

  // Academic validation
  academicValidationRequired: boolean;
  academicAutoApproved: boolean;
  academicValidatedBy?: string;
  academicValidatedAt?: string;
  academicRejectionReason?: string;

  // Document generation
  pdfGeneratedAt?: string;
  pdfPath?: string;
  pdfDownloadedBy?: string;
  pdfDownloadedAt?: string;

  // Physical workflow
  printedBy?: string;
  printedAt?: string;
  deliveredBy?: string;
  deliveredAt?: string;
  deliveryNotes?: string;

  // Metadata
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;

  /** Present on admin list items and on the detail; null when nothing to flag. */
  academicAttention?: AcademicAttention | null;

  // Relations
  academicYear?: {
    id: string;
    name: string;
  };
  student?: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
    studentCode?: string;
    filiereInscrit?: string | null;
    niveauFormation?: number | null;
    status?: string;
    campusId?: string;
    campus?: {
      name: string;
    };
  };
  financeValidator?: {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  academicValidator?: {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  statusHistory?: RequestStatusHistoryItem[];
}

export interface RequestStatusHistoryItem {
  id: string;
  requestId: string;
  fromStatus: RequestStatus;
  toStatus: RequestStatus;
  changedBy?: string;
  changedByRole?: string;
  comment?: string;
  timestamp: string;
  changedByUser?: {
    firstName: string;
    lastName: string;
    role: string;
  };
}

export interface AttestationTypeOption {
  type: AttestationType;
  labelFr: string;
  descriptionFr?: string | null;
  isEligible: boolean;
  eligibilityReason?: string | null;
}

/** An academic year the student is enrolled in (GET /student-requests/years). */
export interface EnrolledYear {
  id: string;
  name: string;
  isCurrent: boolean;
  niveauFormation: number | null;
}

export interface EligibilityCheckResult {
  eligible: boolean;
  /** Why the request would be refused; null when it can be submitted. */
  blockingReason: string | null;
  academicYearId: string;
  academicYearName: string;
  financeEligible: boolean;
  financeAutoApprove: boolean;
  financeReason: string;
  academicEligible: boolean;
  academicAutoApprove: boolean;
  academicReason: string;
  estimatedProcessingTime: string;
}

export interface CreateRequestDto {
  requestType: AttestationType;
  /** Omitted = the student's default year (current if enrolled, else most recent). */
  academicYearId?: string;
  purpose?: string;
}

export interface ValidateRequestDto {
  approved: boolean;
  rejectionReason?: string;
}

export interface RequestFilterDto {
  type?: AttestationType;
  status?: RequestStatus;
  academicYearId?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedRequests {
  data: StudentRequest[];
  total: number;
  page: number;
  totalPages: number;
}

/** Pagination of the admin lists. */
export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
}

export interface PaginatedAdminRequests {
  data: StudentRequest[];
  meta: PaginationMeta;
}

export interface AdminListParams {
  academicYearId?: string;
  page?: number;
  limit?: number;
}

/** Per-tenant rules of one attestation type (GET/PUT /admin/student-requests/config). */
export interface AttestationConfigItem {
  attestation_type: AttestationType;
  label_fr: string;
  description_fr: string | null;
  requires_finance_validation: boolean;
  requires_academic_validation: boolean;
  requires_full_payment: boolean;
  requires_no_rattrapage: boolean;
  requires_good_attendance: boolean;
  minimum_semester_completed: number | null;
  requires_graduation_status: boolean;
  is_active: boolean;
  display_order: number;
  source: 'tenant' | 'default';
}

export type UpdateAttestationConfigDto = Partial<
  Omit<AttestationConfigItem, 'attestation_type' | 'source'>
>;

/** A downloaded document: the bytes and the server-provided file name. */
export interface DownloadedDocument {
  blob: Blob;
  filename: string;
}
